import React, { useEffect, useRef, useState } from "react";

import { CustomButton } from "components/CustomButton";
import { CodePreview } from "./CodeForm";
import {
  CLOSES_IN,
  INDIVIDUAL,
  OPENS_IN,
  PRIVATE,
  dayLabel,
  describeReach,
  draftFromRow,
  renewalPlanFor,
  redemptionTerm,
} from "./draft";

const STATUS = {
  ACTIVE: ["Active", "rc-badge-active"],
  INACTIVE: ["Inactive", "rc-badge-inactive"],
  EXPIRED: ["Expired", "rc-badge-expired"],
  EXHAUSTED: ["Fully used", "rc-badge-exhausted"],
};

export const StatusBadge = ({ status }) => {
  const [label, tone] = STATUS[status] ?? [status, "rc-badge-inactive"];
  return <span className={`rc-badge ${tone}`}>{label}</span>;
};

/** The button says it worked. A toast would pull the eye away from the code. */
const CopyCode = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button type="button" className="rc-link" onClick={copy}>
      {copied ? "Copied" : "Copy code"}
    </button>
  );
};

const History = ({ code, money }) => {
  const isTeacher = code.audience === INDIVIDUAL;
  const uses = code.uses ?? [];

  if (uses.length === 0) return <p className="rc-hint">Nobody has redeemed this code yet.</p>;

  return (
    <div className="table-responsive">
      <table className="rc-history">
        <thead>
          <tr>
            {!isTeacher && <th>School or district</th>}
            <th>Redeemed by</th>
            <th>Date</th>
            <th>Term</th>
            <th>Paid</th>
          </tr>
        </thead>
        <tbody>
          {uses.map((use) => (
            <tr key={use.id}>
              {!isTeacher && <td>{use.organizationName ?? "—"}</td>}
              <td>
                {use.redeemedByUser?.name ?? use.redeemedByEmail}
                {use.redeemedByUser?.name && <small>{use.redeemedByEmail}</small>}
              </td>
              <td>{dayLabel(use.createdAt)}</td>
              <td>{redemptionTerm(use)}</td>
              <td>{money(use.amountCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** One saved code, in the same shell as the form that edits it. */
const CodeDetails = ({ code, loading, plans, money, onClose, onEdit }) => {
  if (loading) {
    return (
      <div className="rc-form">
        <div className="rc-form-main">
          <p className="rc-hint">Loading this code…</p>
        </div>
        <aside className="rc-form-side" />
      </div>
    );
  }

  const draft = draftFromRow(code, plans);
  const plan = plans.find((p) => String(p.id) === String(draft.planId));
  const renewal = renewalPlanFor(draft, plans, plan);
  const emails = code.authorizedEmails ?? [];

  return (
    <div className="rc-form">
      <div className="rc-form-main">
        <header className="rc-details-head">
          <div className="rc-details-title">
            <h2 className="rc-title rc-code-text">{code.code}</h2>
            <StatusBadge status={code.status} />
          </div>
          <CopyCode code={code.code} />
        </header>

        <dl className="rc-facts">
          <div>
            <dt>Who can use it</dt>
            <dd>{describeReach(code)}</dd>
          </div>
          <div>
            <dt>Visibility</dt>
            <dd>{code.visibility === PRIVATE ? "Private" : "Public"}</dd>
          </div>
          <div>
            <dt>Used</dt>
            <dd>
              {code.redeemedCount} of {code.maxRedemptions}
            </dd>
          </div>
          <div>
            <dt>Starts</dt>
            <dd>{dayLabel(code.activatesAt, OPENS_IN) || "Right away"}</dd>
          </div>
          <div>
            <dt>Ends</dt>
            <dd>{dayLabel(code.expiresAt, CLOSES_IN) || "No end date"}</dd>
          </div>
        </dl>

        {emails.length > 0 && (
          <section className="rc-group" aria-labelledby="rc-emails">
            <h3 id="rc-emails">Allowed emails</h3>
            <div className="rc-chips">
              {emails.map((email) => (
                <span key={email} className="rc-chip">
                  {email}
                </span>
              ))}
            </div>
          </section>
        )}

        <section className="rc-group" aria-labelledby="rc-history">
          <h3 id="rc-history">Redemptions</h3>
          <History code={code} money={money} />
        </section>
      </div>

      <aside className="rc-form-side">
        <CodePreview draft={draft} plan={plan} renewal={renewal} money={money} />
        <div className="rc-actions">
          <CustomButton title="Close" color="light" onClick={onClose} />
          <CustomButton title="Edit code" color="primary" onClick={onEdit} />
        </div>
      </aside>
    </div>
  );
};

export default CodeDetails;
